import { FC } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import dayjs from 'dayjs';
import { IHrvSeriesMetric } from '../../../../models/injury';
import { ChartEmptyState } from './_shared/ChartEmptyState';
import {
  chartAxisTick,
  chartGridProps,
  chartTooltipContentStyle,
  chartTooltipItemStyle,
  chartTooltipLabelStyle,
} from '../../../../shared/charts/chartTheme';

interface Props {
  data: IHrvSeriesMetric | null;
  period?: string;
  height?: number;
}

export const HrvSeriesChart: FC<Props> = ({ data, height }) => {
  if (!data || !data.series || data.series.length === 0) {
    return <ChartEmptyState />;
  }

  const gradientId = 'hrvGradient';

  return (
    <ResponsiveContainer width="100%" height={height ?? 220}>
      <AreaChart data={data.series} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--ri-good)" stopOpacity={0.2} />
            <stop offset="95%" stopColor="var(--ri-good)" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid {...chartGridProps} />
        <XAxis dataKey="date" tickFormatter={(v) => dayjs(v).format('DD/MM')} tick={chartAxisTick} />
        <YAxis tick={chartAxisTick} />
        <Tooltip
          contentStyle={chartTooltipContentStyle}
          labelStyle={chartTooltipLabelStyle}
          itemStyle={chartTooltipItemStyle}
          formatter={(value) => [`${value} ms`, 'HRV']}
          labelFormatter={(label) => dayjs(label).format('DD/MM/YYYY')}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke="var(--ri-good)"
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          dot={false}
        />
        {data.injuryMarkers?.map((marker) => (
          <ReferenceLine key={marker.injuryId} x={marker.date} stroke="var(--ri-critical)" strokeWidth={1.5} />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
};
